import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, FormControl, InputLabel,
  Select, MenuItem, CircularProgress, Alert, LinearProgress, Stack,
  Collapse, Link, Chip, Tooltip as MuiTooltip,
} from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { codeExtractionApi, statsApi } from '../../services/api';

const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0', '#607d8b'];

function MetricCard({ title, value, subtitle, color }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="h4" sx={{ color: color || 'primary.main', my: 0.5 }}>
          {value}
        </Typography>
        {subtitle && <Typography variant="caption" color="text.secondary" component="div">{subtitle}</Typography>}
      </CardContent>
    </Card>
  );
}

function CoverageBar({ label, current, total, color }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight="medium">{current}/{total} ({pct}%)</Typography>
      </Box>
      <LinearProgress
        variant="determinate" value={pct}
        sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { backgroundColor: color || '#4caf50' } }}
      />
    </Box>
  );
}

export default function ExtractionStatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retailers, setRetailers] = useState([]);
  const [retailerId, setRetailerId] = useState('');
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (retailerId) params.retailer_id = retailerId;
      const data = await codeExtractionApi.getStats(params);
      setStats(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [retailerId]);

  const toggleBreakdown = useCallback(async () => {
    if (breakdownOpen) { setBreakdownOpen(false); return; }
    setBreakdownOpen(true);
    if (breakdown) return; // cached for this retailer scope
    setBreakdownLoading(true);
    try {
      const params = {};
      if (retailerId) params.retailer_id = retailerId;
      setBreakdown(await codeExtractionApi.getFailedBreakdown(params));
    } catch (err) {
      setError(err.message);
    } finally {
      setBreakdownLoading(false);
    }
  }, [breakdownOpen, breakdown, retailerId]);

  useEffect(() => {
    statsApi.getRetailers().then(setRetailers).catch(() => {});
  }, []);

  useEffect(() => {
    loadStats();
    // Reset the breakdown when the retailer scope changes.
    setBreakdown(null);
    setBreakdownOpen(false);
  }, [loadStats]);

  if (loading && !stats) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  const sourceData = stats ? [
    { name: 'Brand Pattern', value: stats.brand_pattern_count || 0 },
    { name: 'Legacy Pattern', value: stats.pattern_count || 0 },
    { name: 'Legacy Brand Format', value: stats.brand_format_count || 0 },
    { name: 'Heuristic', value: stats.heuristic_count || 0 },
    { name: 'Failed', value: stats.failed_count || 0 },
    { name: 'Not attempted', value: stats.not_attempted || 0 },
  ].filter(d => d.value > 0) : [];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 0 }, mb: 3 }}>
        <Typography variant="h6">Extraction Coverage</Typography>
        <FormControl size="small" sx={{ minWidth: { xs: 0, sm: 200 } }}>
          <InputLabel>Retailer</InputLabel>
          <Select value={retailerId} onChange={(e) => setRetailerId(e.target.value)} label="Retailer">
            <MenuItem value="">All Retailers</MenuItem>
            {retailers.map(r => (
              <MenuItem key={r.id} value={r.id}>{r.name || r.id}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {stats && (
        <>
          {/* Top metric cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard title="Active Products" value={stats.total_products?.toLocaleString() || 0} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard title="Extracted" value={stats.extracted_count?.toLocaleString() || 0} color="#4caf50"
                subtitle={`${stats.extraction_rate || 0}% extraction rate`}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Failed"
                value={stats.failed_count?.toLocaleString() || 0}
                color="#f44336"
                subtitle={stats.failed_has_rules > 0 ? (
                  <Link component="button" type="button" underline="hover" onClick={toggleBreakdown} sx={{ fontSize: 'inherit' }}>
                    {stats.failed_has_rules.toLocaleString()} have rules — {breakdownOpen ? 'hide' : 'break down'}
                  </Link>
                ) : undefined}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <MetricCard
                title="Not Attempted"
                value={stats.not_attempted?.toLocaleString() || 0}
                color="#9e9e9e"
                subtitle="No extraction run yet"
              />
            </Grid>
          </Grid>

          {/* Failed-with-rules breakdown (A2) */}
          <Collapse in={breakdownOpen} unmountOnExit>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>Failed with rules — why?</Typography>
                {breakdownLoading && <LinearProgress sx={{ mb: 2 }} />}
                {breakdown && (
                  <Stack spacing={1.5}>
                    {[
                      {
                        key: 'genuine',
                        label: 'Genuine non-match',
                        color: '#f44336',
                        help: 'Rule exists but its regex no longer fits the data (overfit). Re-infer the rule.',
                        action: 'Use "Re-infer Brittle" on the Brands tab',
                      },
                      {
                        key: 'stale',
                        label: 'Stale',
                        color: '#ff9800',
                        help: "The rule's current regex WOULD match — these products just need a re-extraction run.",
                        action: 'Re-run extraction (Unlock Matching / re-extract)',
                      },
                      {
                        key: 'wrong_field',
                        label: 'No supplier field',
                        color: '#9e9e9e',
                        help: 'The product has no stored supplier code and the retailer has no free-text supplier-code source, so there is nowhere for the extractor to read a code from.',
                        action: 'Set a supplier-code source on the Retailers tab, or have the parser write the supplier code',
                      },
                      {
                        key: 'no_brand',
                        label: 'No brand',
                        color: '#607d8b',
                        help: 'Product has no normalized brand, so extraction is skipped before any rule applies. This is a brand-mapping gap, not an extraction one.',
                        action: 'Fix brand mapping (Brand Mapping tab)',
                      },
                    ].map((b) => (
                      <Box key={b.key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        <Chip
                          label={(breakdown.buckets?.[b.key] || 0).toLocaleString()}
                          sx={{ bgcolor: b.color, color: '#fff', fontWeight: 600, minWidth: 64 }}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">{b.label}</Typography>
                          <Typography variant="caption" color="text.secondary" component="div">{b.help}</Typography>
                          <Typography variant="caption" color="primary" component="div">→ {b.action}</Typography>
                          {breakdown.samples?.[b.key]?.length > 0 && (
                            <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {breakdown.samples[b.key].slice(0, 6).map((s, i) => (
                                <MuiTooltip key={i} title={`${s.normalized_brand} @ ${s.retailer_id}`} arrow>
                                  <Chip size="small" variant="outlined" label={s.raw_product_id} />
                                </MuiTooltip>
                              ))}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    ))}
                    {breakdown.sample_limited && (
                      <Typography variant="caption" color="text.secondary">
                        Counts based on a sample — actual totals may be higher.
                      </Typography>
                    )}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Collapse>

          {/* Extraction breakdown */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>Product Breakdown</Typography>
                  <CoverageBar
                    label="Brand pattern (simplified)"
                    current={stats.brand_pattern_count || 0}
                    total={stats.total_products}
                    color="#4caf50"
                  />
                  {(stats.pattern_count > 0 || stats.brand_format_count > 0) && (
                    <CoverageBar
                      label="Legacy extraction (pattern + brand format)"
                      current={(stats.pattern_count || 0) + (stats.brand_format_count || 0)}
                      total={stats.total_products}
                      color="#ff9800"
                    />
                  )}
                  {stats.heuristic_count > 0 && (
                    <CoverageBar
                      label="Heuristic (unvalidated)"
                      current={stats.heuristic_count}
                      total={stats.total_products}
                      color="#9e9e9e"
                    />
                  )}
                  <CoverageBar
                    label="Failed"
                    current={stats.failed_count || 0}
                    total={stats.total_products}
                    color="#f44336"
                  />
                  {stats.not_attempted > 0 && (
                    <CoverageBar
                      label="No extraction attempt"
                      current={stats.not_attempted}
                      total={stats.total_products}
                      color="#bdbdbd"
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                {/* Extraction source pie chart */}
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Extraction Source</Typography>
                    {sourceData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label>
                            {sourceData.map((_, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No extraction data yet
                      </Typography>
                    )}
                  </CardContent>
                </Card>

                {/* Coverage */}
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>Coverage</Typography>
                    {stats.retailers && (
                      <CoverageBar
                        label="Retailers with a free-text supplier-code source"
                        current={stats.retailers.configured}
                        total={stats.retailers.total}
                        color="#2196f3"
                      />
                    )}
                    {stats.brands && (
                      <CoverageBar
                        label="Brands with extraction pattern"
                        current={stats.brands.with_pattern}
                        total={stats.brands.total}
                        color="#4caf50"
                      />
                    )}
                  </CardContent>
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}
